/**
 * @file bundle-insight.ts
 *
 * Assembles a self-contained Worker bundle for an insight and returns it
 * as a Blob URL ready for `new Worker(url)`. The bundle is two pieces
 * concatenated:
 *
 *   1. The worker runtime (`worker-runtime.ts`) — the `sbi.*` host API,
 *      the main-thread message protocol, and `__sbi_bootstrap`. Imported
 *      via `?prebuilt-js`, so it arrives already transpiled + minified
 *      (see `prebuildRuntimePlugin` in `vite.config.ts`).
 *   2. A `__sbi_bootstrap(async () => { ...insightJs... })` call wrapping
 *      the insight's JS body.
 *
 * The insight JS is now compiled **server-side** at save time and served
 * ready-to-run by `GET /api/embed/insights/:id/source` (plan:
 * `insight-save-and-prebundle.md`). So there is no browser-side
 * transpile anymore — this module used to initialise `esbuild-wasm` and
 * transform both the runtime and the user source on first paint; that
 * whole path (and the ~3 MB gzipped wasm binary it pulled into the
 * element bundle) is gone. `bundleInsight` is now synchronous.
 *
 * Bundles are cached by content hash within the session — one bundle per
 * insight, reused across re-runs (e.g. `sbi.state` changes).
 */

// Transpiled+minified worker runtime, inlined at build time as a string.
import workerRuntimeJs from "./worker-runtime.ts?prebuilt-js";

/**
 * Session-local cache mapping `hashSource(js)` → Blob URL. Hands back the
 * same Worker URL when the same insight JS is bundled again, avoiding
 * duplicate Blobs (each Blob URL pins memory until revoked).
 */
const cache = new Map<string, string>();

/**
 * Fast non-cryptographic FNV-1a 32-bit hash, suffixed with the string's
 * length in hex.
 *
 * Why FNV-1a and not `crypto.subtle.digest`? `crypto.subtle` is only
 * available in secure contexts (HTTPS or localhost). The dev stack
 * serves plain HTTP on a custom wildcard domain, so `crypto.subtle` is
 * undefined and any call would throw. FNV-1a is plenty for a
 * session-local cache key: collisions are harmless (worst case: one
 * insight re-bundled once), and appending the length makes accidental
 * collisions even less likely.
 *
 * @param s - The string to hash (the insight's JS body).
 * @returns An 8-char hex hash followed by `:` and the length in hex.
 */
function hashSource(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0") + ":" + s.length.toString(16);
}

/**
 * Bundle an insight's (already-transpiled) JS into a Blob URL for
 * `new Worker(url)`.
 *
 * @param insightJs - The insight's JS body, as served by
 *   `/api/embed/insights/:id/source` (server-side prebundled; SQL slot
 *   markers already carry per-request UUIDs).
 * @returns A Blob URL owned by the cache. Callers must NOT
 *   `URL.revokeObjectURL` it; use `clearBundleCache()` to release in bulk.
 */
export function bundleInsight(insightJs: string): string {
  const key = hashSource(insightJs);
  const cached = cache.get(key);
  if (cached) return cached;

  // The runtime defines `__sbi_bootstrap`; we call it with an async
  // function whose body is the insight code, giving the runtime full
  // control over when to execute it (e.g. after the init message).
  const wrapped = `${workerRuntimeJs}
__sbi_bootstrap(async () => {
${insightJs}
});`;

  const blob = new Blob([wrapped], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  cache.set(key, url);
  return url;
}

/**
 * Release every Blob URL held by the bundle cache.
 *
 * Blob URLs from `URL.createObjectURL` keep their underlying Blob (and
 * its memory) alive until the document unloads or `URL.revokeObjectURL`
 * is called. In long-lived sessions with many insights — or during HMR
 * in dev — those accumulate. Call this when navigating away from the
 * insights surface.
 */
export function clearBundleCache(): void {
  for (const url of cache.values()) URL.revokeObjectURL(url);
  cache.clear();
}
